import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import api from "../api/axios";

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filterRole, setFilterRole] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [departments, setDepartments] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    employeeId: "",
    role: "",
    department: "",
    designation: "",
    phone: "",
    // Banking Details
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    accountHolderName: "",
    panNumber: "",
    upiId: "",
  });

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
      // Extract unique departments from employees
      const uniqueDepts = [
        ...new Set(response.data.map((emp) => emp.department).filter(Boolean)),
      ];
      setDepartments(uniqueDepts.sort());
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get("/roles");
      setRoles(response.data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", formData);
      setShowModal(false);
      setFormData({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        employeeId: "",
        role: "",
        department: "",
        designation: "",
        phone: "",
        bankName: "",
        accountNumber: "",
        ifscCode: "",
        accountHolderName: "",
        panNumber: "",
        upiId: "",
      });
      fetchEmployees();
    } catch (error) {
      alert(error.response?.data?.message || "Error creating employee");
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.put(`/employees/${id}`, { isActive: !currentStatus });
      fetchEmployees();
    } catch (error) {
      alert("Error updating employee status");
    }
  };

  // View employee details
  const handleViewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowViewModal(true);
  };

  // Open edit modal
  const handleEditEmployee = (employee) => {
    setEditFormData({
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      email: employee.email || "",
      employeeId: employee.employeeId || "",
      role: employee.role?._id || "",
      department: employee.department || "",
      designation: employee.designation || "",
      phone: employee.phone || "",
      bankName: employee.bankName || "",
      accountNumber: employee.accountNumber || "",
      ifscCode: employee.ifscCode || "",
      accountHolderName: employee.accountHolderName || "",
      panNumber: employee.panNumber || "",
      upiId: employee.upiId || "",
    });
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  // Submit edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/employees/${selectedEmployee._id}`, editFormData);
      setShowEditModal(false);
      setSelectedEmployee(null);
      fetchEmployees();
      alert("Employee updated successfully!");
    } catch (error) {
      alert(error.response?.data?.message || "Error updating employee");
    }
  };

  const handleExportEmployee = async (employeeId, employeeName) => {
    try {
      console.log(
        `📊 Exporting data for employee: ${employeeName} (${employeeId})`
      );

      // Fetch employee data from available endpoints with error handling
      let attendanceData = [];
      let leadsData = [];
      let leavesData = [];
      let dealsData = [];
      let projectsData = [];

      try {
        const attendanceRes = await api.get(
          `/attendance?employeeId=${employeeId}`
        );
        attendanceData = Array.isArray(attendanceRes.data)
          ? attendanceRes.data
          : [];
        console.log(`✓ Attendance: ${attendanceData.length} records`);
      } catch (err) {
        console.log("⚠ Attendance data not available:", err.message);
      }

      try {
        const leavesRes = await api.get(`/leaves?employeeId=${employeeId}`);
        leavesData = Array.isArray(leavesRes.data) ? leavesRes.data : [];
        console.log(`✓ Leaves: ${leavesData.length} records`);
      } catch (err) {
        console.log("⚠ Leaves data not available:", err.message);
      }

      try {
        // Get assigned leads for this employee
        const leadsRes = await api.get(
          `/lead-assignments/my-leads?employeeId=${employeeId}`
        );
        leadsData = Array.isArray(leadsRes.data) ? leadsRes.data : [];
        console.log(`✓ Leads: ${leadsData.length} records`);
      } catch (err) {
        console.log("⚠ Leads data not available:", err.message);
      }

      try {
        const dealsRes = await api.get(`/deals`);
        // Filter deals by assigned employee
        dealsData = Array.isArray(dealsRes.data)
          ? dealsRes.data.filter(
            (d) =>
              d.assignedTo &&
              (d.assignedTo._id === employeeId || d.assignedTo === employeeId)
          )
          : [];
        console.log(`✓ Deals: ${dealsData.length} records`);
      } catch (err) {
        console.log("⚠ Deals data not available:", err.message);
      }

      try {
        const projectsRes = await api.get(`/projects`);
        // Filter projects by assigned employees
        projectsData = Array.isArray(projectsRes.data)
          ? projectsRes.data.filter(
            (p) =>
              p.assignedEmployees &&
              p.assignedEmployees.some(
                (emp) => emp._id === employeeId || emp === employeeId
              )
          )
          : [];
        console.log(`✓ Projects: ${projectsData.length} records`);
      } catch (err) {
        console.log("⚠ Projects data not available:", err.message);
      }

      // Create PDF document
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let yPosition = 20;
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      const lineHeight = 7;

      // Helper function to add new page if needed
      const addNewPageIfNeeded = (spaceNeeded) => {
        if (yPosition + spaceNeeded > pageHeight - 10) {
          pdf.addPage();
          yPosition = 20;
        }
      };

      // Title
      pdf.setFontSize(16);
      pdf.setFont(undefined, "bold");
      pdf.text("Employee Report", margin, yPosition);
      yPosition += 12;

      // Employee Info Header
      pdf.setFontSize(11);
      pdf.setFont(undefined, "bold");
      pdf.text(`Name: ${employeeName}`, margin, yPosition);
      yPosition += lineHeight;
      pdf.setFont(undefined, "normal");
      pdf.setFontSize(9);
      pdf.text(
        `Generated: ${new Date().toLocaleDateString("en-IN")}`,
        margin,
        yPosition
      );
      yPosition += 12;

      // Attendance Section - Only if data exists
      if (attendanceData.length > 0) {
        addNewPageIfNeeded(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text("Attendance Records", margin, yPosition);
        yPosition += 8;

        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);
        const attendanceTableData = attendanceData.map((att) => [
          att.date ? new Date(att.date).toLocaleDateString("en-IN") : "N/A",
          att.status === "present"
            ? "Present"
            : att.status === "absent"
              ? "Absent"
              : "Half-day",
          att.workingHours ? att.workingHours.toString() : "0",
        ]);

        if (attendanceTableData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            head: [["Date", "Status", "Hours"]],
            body: attendanceTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [41, 128, 185],
              textColor: 255,
              fontStyle: "bold",
            },
          });
          yPosition = pdf.lastAutoTable.finalY + 8;
        }
      }

      // Leaves Section - Only if data exists
      if (leavesData.length > 0) {
        addNewPageIfNeeded(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text("Leave Applications", margin, yPosition);
        yPosition += 8;

        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);
        const leaveTableData = leavesData.map((leave) => [
          leave.startDate
            ? new Date(leave.startDate).toLocaleDateString("en-IN")
            : "N/A",
          leave.endDate
            ? new Date(leave.endDate).toLocaleDateString("en-IN")
            : "N/A",
          leave.leaveType || "N/A",
          leave.status || "Pending",
        ]);

        if (leaveTableData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            head: [["Start", "End", "Type", "Status"]],
            body: leaveTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [46, 204, 113],
              textColor: 255,
              fontStyle: "bold",
            },
          });
          yPosition = pdf.lastAutoTable.finalY + 8;
        }
      }

      // Assigned Leads Section - Only if data exists
      if (leadsData.length > 0) {
        addNewPageIfNeeded(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text("Assigned Leads/Clients", margin, yPosition);
        yPosition += 8;

        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);
        const leadsTableData = leadsData.map((lead) => [
          lead.leadName || "N/A",
          lead.contactNumber || "N/A",
          lead.email || "N/A",
          lead.status || "New",
        ]);

        if (leadsTableData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            head: [["Lead Name", "Contact", "Email", "Status"]],
            body: leadsTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [241, 196, 15],
              textColor: 0,
              fontStyle: "bold",
            },
          });
          yPosition = pdf.lastAutoTable.finalY + 8;
        }
      }

      // Deals Section - Only if data exists
      if (dealsData.length > 0) {
        addNewPageIfNeeded(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text("Deals", margin, yPosition);
        yPosition += 8;

        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);
        const dealsTableData = dealsData.map((deal) => [
          deal.dealName || "N/A",
          deal.dealValue ? `₹${deal.dealValue.toLocaleString()}` : "N/A",
          deal.stage || "N/A",
          deal.status || "Active",
        ]);

        if (dealsTableData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            head: [["Deal Name", "Value", "Stage", "Status"]],
            body: dealsTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [230, 126, 34],
              textColor: 255,
              fontStyle: "bold",
            },
          });
          yPosition = pdf.lastAutoTable.finalY + 8;
        }
      }

      // Projects Section - Only if data exists
      if (projectsData.length > 0) {
        addNewPageIfNeeded(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, "bold");
        pdf.text("Projects", margin, yPosition);
        yPosition += 8;

        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);
        const projectsTableData = projectsData.map((project) => [
          project.name || "N/A",
          project.status || "Active",
          project.deadline
            ? new Date(project.deadline).toLocaleDateString("en-IN")
            : "N/A",
        ]);

        if (projectsTableData.length > 0) {
          autoTable(pdf, {
            startY: yPosition,
            head: [["Project Name", "Status", "Deadline"]],
            body: projectsTableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: {
              fillColor: [155, 89, 182],
              textColor: 255,
              fontStyle: "bold",
            },
          });
          yPosition = pdf.lastAutoTable.finalY + 8;
        }
      }

      // Summary Section
      addNewPageIfNeeded(10);
      pdf.setFontSize(11);
      pdf.setFont(undefined, "bold");
      pdf.text("Summary", margin, yPosition);
      yPosition += 8;
      pdf.setFont(undefined, "normal");
      pdf.setFontSize(9);

      const summaryData = [
        ["Attendance Records", attendanceData.length],
        ["Leave Applications", leavesData.length],
        ["Assigned Leads", leadsData.length],
        ["Deals", dealsData.length],
        ["Projects", projectsData.length],
      ];

      autoTable(pdf, {
        startY: yPosition,
        head: [["Item", "Count"]],
        body: summaryData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: {
          fillColor: [52, 73, 94],
          textColor: 255,
          fontStyle: "bold",
        },
      });

      // Footer
      pdf.setFontSize(8);
      pdf.setFont(undefined, "italic");
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      // Download PDF
      const filename = `${employeeName.replace(/\s+/g, "_")}_Report_${new Date().toISOString().split("T")[0]
        }.pdf`;
      pdf.save(filename);
      console.log(`✓ PDF exported: ${filename}`);
      alert("Report downloaded successfully!");
    } catch (error) {
      console.error("❌ Error exporting employee data:", error);
      alert("Error generating report. Please try again.");
    }
  };

  // Filter employees based on selected role, department, and search term
  const filteredEmployees = employees.filter((employee) => {
    const roleMatch = !filterRole || employee.role?._id === filterRole;
    const deptMatch =
      !filterDepartment || employee.department === filterDepartment;
    const nameMatch =
      !searchTerm ||
      (employee.firstName + " " + employee.lastName)
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    return roleMatch && deptMatch && nameMatch;
  });

  const handleExportListExcel = async () => {
    try {
      const response = await api.get('/employees/export/excel', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Employees_List.xlsx`;
      link.click();
    } catch (error) {
      console.error('Error exporting excel:', error);
      alert('Error exporting Excel file');
    }
  };

  const handleExportListWord = async () => {
    try {
      const response = await api.get('/employees/export/word', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Employees_List.docx`;
      link.click();
    } catch (error) {
      console.error('Error exporting word:', error);
      alert('Error exporting Word file');
    }
  };

  const handleExportListPDF = async () => {
    try {
      const response = await api.get('/employees/export/pdf', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Employees_List.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF file');
    }
  };

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Employee Management</h1>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <input
            type="text"
            placeholder="Search employees by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 15px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              fontSize: "14px",
              minWidth: "250px",
            }}
          />
          <button
            className="btn btn-success"
            onClick={handleExportListExcel}
          >
            Excel
          </button>
          <button
            className="btn btn-info"
            onClick={handleExportListWord}
          >
            Word
          </button>
          <button
            className="btn btn-danger"
            onClick={handleExportListPDF}
          >
            PDF
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            Add Employee
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="card" style={{ marginBottom: "20px", padding: "15px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            alignItems: "flex-end",
          }}
        >
          <div className="form-group" style={{ marginBottom: "0" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Filter by Role
            </label>
            <select
              className="form-control"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: "0" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "500",
              }}
            >
              Filter by Department
            </label>
            <select
              className="form-control"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: "hidden", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ marginBottom: 0 }}>
            <thead>
              <tr style={{ background: "#286cd2ff" }}>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Employee ID</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Name</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Email</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Role</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Department</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "none" }}>Status</th>
                <th style={{ color: "#000000", fontWeight: "700", padding: "16px", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center", borderBottom: "none" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee, index) => (
                <tr key={employee._id} style={{
                  background: index % 2 === 0 ? "#fff" : "#f8f9ff",
                  transition: "all 0.2s ease",
                  borderBottom: "1px solid #eee"
                }}>
                  <td style={{ padding: "12px 16px", fontWeight: "500", color: "#667eea", fontSize: "13px" }}>
                    {employee.employeeId}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontWeight: "600",
                        fontSize: "14px"
                      }}>
                        {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: "600", color: "#333", fontSize: "14px" }}>
                          {employee.firstName} {employee.lastName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#888" }}>
                          {employee.designation || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "13px", color: "#555" }}>{employee.email}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      background: "#e8f4fd",
                      color: "#1976d2",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "500"
                    }}>
                      {employee.role?.name}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: "13px", color: "#555" }}>{employee.department || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      background: employee.isActive ? "#e8f5e9" : "#ffebee",
                      color: employee.isActive ? "#2e7d32" : "#c62828",
                      padding: "4px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}>
                      {employee.isActive ? "● Active" : "● Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                      <button
                        onClick={() => handleViewEmployee(employee)}
                        title="View"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          border: "none",
                          background: "#f0f0f0",
                          color: "#555",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          transition: "all 0.2s"
                        }}
                      >
                        👁️
                      </button>
                      <button
                        onClick={() => handleEditEmployee(employee)}
                        title="Edit"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          border: "none",
                          background: "#fff3e0",
                          color: "#f57c00",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          transition: "all 0.2s"
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleExportEmployee(employee._id, `${employee.firstName} ${employee.lastName}`)}
                        title="Export"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          border: "none",
                          background: "#e3f2fd",
                          color: "#1976d2",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          transition: "all 0.2s"
                        }}
                      >
                        📥
                      </button>
                      <button
                        onClick={() => handleToggleStatus(employee._id, employee.isActive)}
                        title={employee.isActive ? "Deactivate" : "Activate"}
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          border: "none",
                          background: employee.isActive ? "#ffebee" : "#e8f5e9",
                          color: employee.isActive ? "#c62828" : "#2e7d32",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          transition: "all 0.2s"
                        }}
                      >
                        {employee.isActive ? "🚫" : "✅"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length === 0 && (
          <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>
            <div style={{ fontSize: "48px", marginBottom: "10px" }}>👥</div>
            <p style={{ margin: 0, fontSize: "16px" }}>No employees found</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Add Employee</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Employee ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.employeeId}
                    onChange={(e) =>
                      setFormData({ ...formData, employeeId: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    className="form-control"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Designation</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.designation}
                    onChange={(e) =>
                      setFormData({ ...formData, designation: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Banking Details Section */}
              <div style={{ marginTop: "20px", marginBottom: "15px" }}>
                <h4 style={{ borderBottom: "1px solid #ddd", paddingBottom: "8px", color: "#333" }}>
                  Banking Details (Optional)
                </h4>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter bank name"
                    value={formData.bankName}
                    onChange={(e) =>
                      setFormData({ ...formData, bankName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name as per bank account"
                    value={formData.accountHolderName}
                    onChange={(e) =>
                      setFormData({ ...formData, accountHolderName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter account number"
                    value={formData.accountNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, accountNumber: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter IFSC code"
                    value={formData.ifscCode}
                    onChange={(e) =>
                      setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter PAN number"
                    value={formData.panNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter UPI ID (e.g., name@upi)"
                    value={formData.upiId}
                    onChange={(e) =>
                      setFormData({ ...formData, upiId: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="flex-end mt-20">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Employee Modal */}
      {showViewModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
            <div style={{
              background: "linear-gradient(135deg, #a8adc3ff 0%, #764ba2 100%)",
              padding: "20px",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <div>
                <h2 style={{ margin: 0, color: "#fff", fontSize: "20px", fontWeight: "600" }}>
                  👤 {selectedEmployee.firstName} {selectedEmployee.lastName}
                </h2>
                <p style={{ margin: "5px 0 0 0", color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                  {selectedEmployee.designation || "Employee"} • {selectedEmployee.department || "General"}
                </p>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#fff",
                  fontSize: "24px",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              {/* Basic Info */}
              <h4 style={{ borderBottom: "2px solid #007bff", paddingBottom: "8px", marginBottom: "15px", color: "#007bff" }}>
                Basic Information
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
                <p><strong>Name:</strong> {selectedEmployee.firstName} {selectedEmployee.lastName}</p>
                <p><strong>Employee ID:</strong> {selectedEmployee.employeeId}</p>
                <p><strong>Email:</strong> {selectedEmployee.email}</p>
                <p><strong>Phone:</strong> {selectedEmployee.phone || "N/A"}</p>
                <p><strong>Role:</strong> {selectedEmployee.role?.name || "N/A"}</p>
                <p><strong>Department:</strong> {selectedEmployee.department || "N/A"}</p>
                <p><strong>Designation:</strong> {selectedEmployee.designation || "N/A"}</p>
                <p><strong>Status:</strong>
                  <span className={`badge ${selectedEmployee.isActive ? "badge-success" : "badge-danger"}`} style={{ marginLeft: "8px" }}>
                    {selectedEmployee.isActive ? "Active" : "Inactive"}
                  </span>
                </p>
              </div>

              {/* Banking Details */}
              <h4 style={{ borderBottom: "2px solid #28a745", paddingBottom: "8px", marginBottom: "15px", color: "#28a745" }}>
                Banking Details
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <p><strong>Bank Name:</strong> {selectedEmployee.bankName || "N/A"}</p>
                <p><strong>Account Holder:</strong> {selectedEmployee.accountHolderName || "N/A"}</p>
                <p><strong>Account Number:</strong> {selectedEmployee.accountNumber || "N/A"}</p>
                <p><strong>IFSC Code:</strong> {selectedEmployee.ifscCode || "N/A"}</p>
                <p><strong>PAN Number:</strong> {selectedEmployee.panNumber || "N/A"}</p>
                <p><strong>UPI ID:</strong> {selectedEmployee.upiId || "N/A"}</p>
              </div>
            </div>

            <div className="flex-end" style={{ padding: "15px", borderTop: "1px solid #ddd" }}>
              <button
                className="btn btn-warning"
                onClick={() => {
                  setShowViewModal(false);
                  handleEditEmployee(selectedEmployee);
                }}
                style={{ marginRight: "10px" }}
              >
                ✏️ Edit Employee
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px", maxHeight: "90vh", overflow: "auto" }}>
            <div style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              padding: "20px",
              borderRadius: "8px 8px 0 0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <div>
                <h2 style={{ margin: 0, color: "#fff", fontSize: "20px", fontWeight: "600" }}>
                  ✏️ Edit Employee
                </h2>
                <p style={{ margin: "5px 0 0 0", color: "rgba(255,255,255,0.9)", fontSize: "15px", fontWeight: "500" }}>
                  {selectedEmployee.firstName} {selectedEmployee.lastName} ({selectedEmployee.employeeId})
                </p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#fff",
                  fontSize: "24px",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.firstName}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, firstName: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.lastName}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, lastName: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editFormData.email}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Employee ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.employeeId}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, employeeId: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    className="form-control"
                    value={editFormData.role}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, role: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.department}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, department: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Designation</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.designation}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, designation: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={editFormData.phone}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, phone: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Banking Details Section */}
              <div style={{ marginTop: "20px", marginBottom: "15px" }}>
                <h4 style={{ borderBottom: "1px solid #ddd", paddingBottom: "8px", color: "#333" }}>
                  Banking Details
                </h4>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter bank name"
                    value={editFormData.bankName}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, bankName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Account Holder Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name as per bank account"
                    value={editFormData.accountHolderName}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, accountHolderName: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter account number"
                    value={editFormData.accountNumber}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, accountNumber: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter IFSC code"
                    value={editFormData.ifscCode}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, ifscCode: e.target.value.toUpperCase() })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter PAN number"
                    value={editFormData.panNumber}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, panNumber: e.target.value.toUpperCase() })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>UPI ID</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter UPI ID (e.g., name@upi)"
                    value={editFormData.upiId}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, upiId: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="flex-end mt-20">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Employees;
